import type { IterableLike } from "../types/IterableLike"
import { Identity } from "@pdfking/tfunc"
import { scan } from "./scan"

/**
 * Removes duplicates from iterable.
 *
 * Uses key function to determine uniqueness. Keeps last occurrence.
 *
 * @param iterable - Array, object, or primitive
 * @param f - Key function (default: Identity)
 * @returns Array with duplicates removed
 *
 * @example
 * ```ts
 * import { delete_duplicates } from "@pdfking/rfunc"
 *
 * delete_duplicates([1, 2, 1, 3]) // [2, 1, 3]
 * delete_duplicates([{ a: 1 }, { a: 1 }, { a: 2 }], v => v.a) // [{ a: 1 }, { a: 2 }]
 * ```
 */
export const delete_duplicates = <T, K = T>(
  iterable: IterableLike<T>,
  f: (item: T) => K = Identity as (item: T) => K
): T[] => {
  const map = new Map<K, T>()

  scan((item: T) => map.set(f(item), item), iterable as T[])

  return [...map.values()]
}
